import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 24px;
  min-height: calc(100vh - 60px);
  background-color: ${({ theme }) => theme.colors.background};
  animation: ${fadeIn} 0.25s ease-out;
`;

export const PageHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

export const PageTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  letter-spacing: -0.3px;
`;

export const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.colors.text}14;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text}28;
    color: ${({ theme }) => theme.colors.text};
  }
  &:active { transform: scale(0.97); }
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;
  margin-bottom: 28px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const ScoreCard = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: 16px;
  border: 1.5px solid ${({ theme }) => theme.colors.shapeGray};
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

export const ScoreNumber = styled.span`
  font-size: 52px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1;
  letter-spacing: -2px;
`;

export const ScoreStars = styled.div`
  display: flex;
  gap: 3px;
  font-size: 20px;
`;

export const ScoreStar = styled.span`
  color: ${({ $filled }) => ($filled ? '#f5a623' : '#ddd')};
`;

export const ScoreLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

export const DistCard = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: 16px;
  border: 1.5px solid ${({ theme }) => theme.colors.shapeGray};
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
`;

export const DistRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border-radius: 6px;
  padding: 2px 4px;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.shapeGray}80;
  }
`;

export const DistStarLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  min-width: 18px;
  text-align: right;
`;

export const DistStarIcon = styled.span`
  font-size: 14px;
  color: #f5a623;
`;

export const BarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: ${({ theme }) => theme.colors.shapeGray};
  border-radius: 99px;
  overflow: hidden;
`;

export const BarFill = styled.div`
  height: 100%;
  border-radius: 99px;
  background: ${({ $pct }) =>
    $pct >= 80 ? '#22c55e' :
    $pct >= 60 ? '#84cc16' :
    $pct >= 40 ? '#f5a623' :
    $pct >= 20 ? '#f97316' : '#ef4444'};
  width: ${({ $pct }) => $pct}%;
  transition: width 0.5s ease;
`;

export const DistCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 28px;
  text-align: right;
`;

export const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

export const FilterChip = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  border-radius: 99px;
  border: 1.5px solid ${({ $active, theme }) =>
    $active ? '#f5a623' : theme.colors.shapeGray};
  background: ${({ $active }) => ($active ? '#f5a62318' : 'transparent')};
  color: ${({ $active, theme }) =>
    $active ? '#f5a623' : theme.colors.textSecondary};
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #f5a623;
    color: #f5a623;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
`;

export const ReviewCard = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: 14px;
  border: 1.5px solid ${({ theme }) => theme.colors.shapeGray};
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 16px ${({ theme }) => theme.colors.text}10;
  }
`;

export const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const StarRow = styled.div`
  display: flex;
  gap: 2px;
`;

export const Star = styled.span`
  font-size: 18px;
  color: ${({ $filled }) => ($filled ? '#f5a623' : '#e0e0e0')};
  line-height: 1;
`;

export const NotaBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 99px;
  background: ${({ $nota }) =>
    $nota >= 4 ? '#22c55e18' :
    $nota === 3 ? '#f5a62318' : '#ef444418'};
  color: ${({ $nota }) =>
    $nota >= 4 ? '#16a34a' :
    $nota === 3 ? '#d97706' : '#dc2626'};
`;

export const CommentText = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.6;
  font-style: italic;

  &::before { content: '"'; }
  &::after  { content: '"'; }
`;

export const NoComment = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
`;

export const ClientRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ClientAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.shapeGray};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-shrink: 0;
  text-transform: uppercase;
`;

export const ClientName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.shapeGray};
`;

export const DateText = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 20px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const EmptyIcon = styled.div`
  font-size: 48px;
  opacity: 0.4;
`;

export const EmptyText = styled.p`
  font-size: 15px;
  font-weight: 500;
  margin: 0;
  opacity: 0.6;
`;

export const EmptySub = styled.p`
  font-size: 13px;
  margin: 0;
  opacity: 0.4;
  text-align: center;
`;
